// The arithmetic of the capture worklet (APP-104 §8), kept apart so it can be tested: whatever
// rate the browser's audio runs at, out come 16 kHz, 16-bit, mono samples in 100 ms frames.
// Shared by the worklet (audio thread) and the microphone (page), so it holds no DOM and imports
// nothing: the worklet is bundled on its own and stays small.

/** The rate the voice socket takes (`FEEDBACK_VOICE_LIMITS.sampleRate`; a test keeps them equal). */
export const SAMPLE_RATE = 16_000;

/** The worklet's registered name. */
export const CAPTURE_PROCESSOR = 'myrewards-voice-capture';

/** 100 ms at 16 kHz: 1,600 samples, 3,200 bytes (Task 16's planned frame). */
export const FRAME_SAMPLES = SAMPLE_RATE / 10;

/** Asks the worklet for its last partial frame; it answers with the frame, then this. */
export const FLUSH = 'flush';
export const FLUSHED = 'flushed';

const toInt16 = (x: number): number => {
  const v = Math.max(-1, Math.min(1, x));
  return v < 0 ? Math.round(v * 32_768) : Math.round(v * 32_767);
};

/**
 * Averages each 1/16,000 s window of the input into one sample (a box filter, enough to keep
 * speech clean of aliasing at 44.1 or 48 kHz), and packs the samples into 100 ms frames. A rate
 * that isn't a whole multiple (44.1 kHz) steps by a fraction; a rate below 16 kHz repeats samples.
 */
export class FrameBuilder {
  /** Input samples per output sample. */
  private readonly step: number;
  /** Input samples seen so far. */
  private seen = 0;
  /** The output sample being averaged, and its running sum. */
  private bucket = 0;
  private sum = 0;
  private count = 0;
  private frame = new Int16Array(FRAME_SAMPLES);
  private filled = 0;

  constructor(inputRate: number) {
    this.step = inputRate / SAMPLE_RATE;
  }

  /** One render quantum in; any frames it completed out (as transferable buffers). */
  push(input: Float32Array): ArrayBuffer[] {
    const out: ArrayBuffer[] = [];
    for (const x of input) {
      const bucket = Math.floor(this.seen / this.step);
      if (bucket !== this.bucket) {
        const value = this.count > 0 ? this.sum / this.count : 0;
        // Below 16 kHz an input sample spans more than one output sample: repeat it.
        for (let b = this.bucket; b < bucket; b += 1) this.emit(value, out);
        this.bucket = bucket;
        this.sum = 0;
        this.count = 0;
      }
      this.sum += x;
      this.count += 1;
      this.seen += 1;
    }
    return out;
  }

  /** What's left of the frame being filled (null when nothing is). */
  flush(): ArrayBuffer | null {
    // The sample still being averaged goes in too.
    if (this.count > 0) {
      const out: ArrayBuffer[] = [];
      this.emit(this.sum / this.count, out);
      this.bucket += 1;
      this.sum = 0;
      this.count = 0;
      if (out[0] !== undefined) return out[0];
    }
    if (this.filled === 0) return null;
    const last = this.frame.slice(0, this.filled).buffer;
    this.filled = 0;
    return last;
  }

  private emit(value: number, out: ArrayBuffer[]): void {
    this.frame[this.filled] = toInt16(value);
    this.filled += 1;
    if (this.filled === FRAME_SAMPLES) {
      out.push(this.frame.buffer);
      this.frame = new Int16Array(FRAME_SAMPLES);
      this.filled = 0;
    }
  }
}
